import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  fetchTelegramOidcNonce,
  loadTelegramLoginLibrary,
  requestTelegramIdToken,
} from "api/TelegramAuth";

type LoadState = "loading" | "ready" | "failed";

/** The API keeps a nonce for 600 s. Refresh a held one at 8 min (timer, or on
 *  focus / visibility when a throttled background timer did not fire)... */
export const NONCE_REFRESH_MS = 8 * 60 * 1000;
/** ...and never open the popup on one older than 9 min: it could expire while
 *  the user picks an account, and the whole popup would end in a 401. */
export const NONCE_MAX_AGE_MS = 9 * 60 * 1000;
/** The popup's callback is not guaranteed (a closed popup may never call
 *  back): stop waiting after this long. */
export const POPUP_WATCHDOG_MS = 2 * 60 * 1000;
/** After the window regains focus from the popup, give a late callback this
 *  long to arrive before the attempt is treated as closed. */
export const POPUP_FOCUS_GRACE_MS = 1500;

interface HeldNonce {
  value: string;
  fetchedAt: number;
}

/** Positive integer Client ID, or null (flag off → no Telegram anywhere). */
export const telegramLoginClientId = (): number | null => {
  const raw = process.env.REACT_APP_TELEGRAM_LOGIN_CLIENT_ID;
  if (!raw) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export interface TelegramPopupToken {
  idToken: string;
  user?: TelegramLoginUser;
}

/** Where a failure came from. The wording stays with the caller. */
export interface TelegramPopupFailure {
  phase: "nonce" | "library" | "token";
  error: unknown;
}

interface UseTelegramOidcPopupOptions {
  clientId: number;
  /** What this popup was for. A rejection is reported as phase "token", and a
   *  fresh nonce is fetched. Also runs for a token that arrives after unmount:
   *  whether that still counts is the caller's decision. */
  onToken: (token: TelegramPopupToken) => Promise<void>;
  onError: (failure: TelegramPopupFailure) => void;
}

/**
 * Telegram Login popup (OpenID Connect): the shared part of signing in and of
 * linking. Spec: specs/active/2026-10-27-telegram-oidc-login (Step 9).
 *
 * The library and a nonce are fetched on mount, so `open` can open the popup
 * synchronously inside the click: a popup opened after an `await` is blocked. A
 * nonce is single-use and short-lived: it is refreshed before it ages out, and a
 * click on a missing or old one fetches a new one instead of opening the popup
 * (the next click opens it). A failed fetch is reported only on a click, never
 * on mount: people signing in another way must not see it. The same holds for
 * the library (blocked by CSP, an adblocker or the network): a mount failure is
 * silent; a click retries the load and reports only if that retry fails. The
 * popup cannot open in that click (the load is async), so the next click opens
 * it.
 */
export const useTelegramOidcPopup = ({
  clientId,
  onToken,
  onError,
}: UseTelegramOidcPopupOptions) => {
  const intl = useIntl();

  const [libraryState, setLibraryState] = useState<LoadState>("loading");
  const [nonceState, setNonceState] = useState<LoadState>("loading");
  const [busy, setBusyState] = useState(false);
  const busyRef = useRef(false);
  const nonceRef = useRef<HeldNonce | null>(null);
  const nonceFetchSeqRef = useRef(0);
  const refreshTimerRef = useRef<number | undefined>(undefined);
  const mountedRef = useRef(true);
  /** Id of the attempt waiting on the popup's callback; 0 = none. */
  const waitingAttemptRef = useRef(0);
  const attemptSeqRef = useRef(0);
  const attemptBlurredRef = useRef(false);
  const watchdogTimerRef = useRef<number | undefined>(undefined);
  const focusGraceTimerRef = useRef<number | undefined>(undefined);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const setBusy = useCallback((value: boolean) => {
    busyRef.current = value;
    setBusyState(value);
  }, []);

  /**
   * Fetch a nonce. `background`: keep the held one usable until the new one
   * lands (a failed background refresh keeps it; its age is re-checked on
   * click). Otherwise drop it and show loading. `report`: toast a failure —
   * only when the user clicked.
   */
  const fetchNonce = useCallback(
    async ({ background, report }: { background: boolean; report: boolean }) => {
      const seq = ++nonceFetchSeqRef.current;
      window.clearTimeout(refreshTimerRef.current);
      if (!background) {
        nonceRef.current = null;
        setNonceState("loading");
      }
      try {
        const value = await fetchTelegramOidcNonce();
        if (!mountedRef.current || seq !== nonceFetchSeqRef.current) return;
        nonceRef.current = { value, fetchedAt: Date.now() };
        setNonceState("ready");
        refreshTimerRef.current = window.setTimeout(() => {
          if (!busyRef.current) fetchNonce({ background: true, report: false });
        }, NONCE_REFRESH_MS);
      } catch (error: any) {
        if (!mountedRef.current || seq !== nonceFetchSeqRef.current) return;
        if (background && nonceRef.current) return;
        nonceRef.current = null;
        setNonceState("failed");
        if (report) onErrorRef.current({ phase: "nonce", error });
      }
    },
    []
  );

  const stopWaiting = useCallback(() => {
    waitingAttemptRef.current = 0;
    window.clearTimeout(watchdogTimerRef.current);
    window.clearTimeout(focusGraceTimerRef.current);
  }, []);

  /** The popup never called back: free the button and get a fresh nonce. */
  const abandonAttempt = useCallback(
    (id: number) => {
      if (!mountedRef.current || waitingAttemptRef.current !== id) return;
      stopWaiting();
      setBusy(false);
      fetchNonce({ background: false, report: false });
    },
    [fetchNonce, setBusy, stopWaiting]
  );

  useEffect(() => {
    // Per effect run, not the shared ref: under StrictMode the first run's
    // cleanup happens before the shared promise settles, and both runs would
    // otherwise see mountedRef === true.
    let cancelled = false;
    mountedRef.current = true;
    loadTelegramLoginLibrary().then(
      () => {
        if (!cancelled) setLibraryState("ready");
      },
      () => {
        // Silent on page load; a click retries and reports.
        if (!cancelled) setLibraryState("failed");
      }
    );
    fetchNonce({ background: false, report: false });

    const onBlur = () => {
      if (waitingAttemptRef.current) attemptBlurredRef.current = true;
    };
    const onWake = () => {
      if (document.visibilityState === "hidden") return;
      // Focus back from the popup with no callback yet: it was most likely
      // closed. Allow a late callback a moment, then free the button.
      const waiting = waitingAttemptRef.current;
      if (waiting && attemptBlurredRef.current) {
        window.clearTimeout(focusGraceTimerRef.current);
        focusGraceTimerRef.current = window.setTimeout(
          () => abandonAttempt(waiting),
          POPUP_FOCUS_GRACE_MS
        );
        return;
      }
      // Background timers are throttled: re-check the nonce's age here.
      const held = nonceRef.current;
      if (!busyRef.current && held && Date.now() - held.fetchedAt >= NONCE_REFRESH_MS) {
        fetchNonce({ background: true, report: false });
      }
    };
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);
    return () => {
      cancelled = true;
      mountedRef.current = false;
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
      window.clearTimeout(refreshTimerRef.current);
      window.clearTimeout(watchdogTimerRef.current);
      window.clearTimeout(focusGraceTimerRef.current);
    };
    // Mount-only: the library and the first nonce are fetched once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** A click on a failed library load: retry it (loading state) and report
   *  only this failure. A nonce that also failed is refetched silently. */
  const retryLibrary = () => {
    setLibraryState("loading");
    if (!nonceRef.current && nonceState === "failed") {
      fetchNonce({ background: false, report: false });
    }
    loadTelegramLoginLibrary().then(
      () => {
        if (mountedRef.current) setLibraryState("ready");
      },
      (error) => {
        if (!mountedRef.current) return;
        setLibraryState("failed");
        onErrorRef.current({ phase: "library", error });
      }
    );
  };

  const open = () => {
    if (busy || libraryState === "loading" || nonceState === "loading") return;
    if (libraryState === "failed") {
      retryLibrary();
      return;
    }
    const held = nonceRef.current;
    if (!held || Date.now() - held.fetchedAt >= NONCE_MAX_AGE_MS) {
      // No popup on a missing or old nonce: fetch one, the next click opens
      // Telegram (a popup opened after an await would be blocked).
      fetchNonce({ background: false, report: true });
      return;
    }

    nonceRef.current = null;
    window.clearTimeout(refreshTimerRef.current);
    const id = ++attemptSeqRef.current;
    waitingAttemptRef.current = id;
    attemptBlurredRef.current = false;
    setBusy(true);
    watchdogTimerRef.current = window.setTimeout(
      () => abandonAttempt(id),
      POPUP_WATCHDOG_MS
    );

    let finished = false;
    let lateCancel = false;
    // Called synchronously, inside the click: opens the popup now.
    requestTelegramIdToken({
      client_id: clientId,
      scope: ["profile"],
      lang: intl.locale,
      nonce: held.value,
    })
      .then(async (result) => {
        const wasWaiting = waitingAttemptRef.current === id;
        if (wasWaiting) stopWaiting();
        if (result.kind === "cancelled") {
          // A cancel after we stopped waiting changes nothing.
          lateCancel = !wasWaiting;
          return;
        }
        // A token that arrives after the watchdog is still a real answer. So
        // is one that arrives after unmount: onToken decides what it means;
        // only this hook's own state updates are skipped.
        if (!wasWaiting && mountedRef.current) setBusy(true);
        await onTokenRef.current({ idToken: result.idToken, user: result.user });
        finished = true;
      })
      .catch((error) => {
        if (waitingAttemptRef.current === id) stopWaiting();
        if (mountedRef.current) onErrorRef.current({ phase: "token", error });
      })
      .finally(() => {
        // Another popup is open now: leave its state alone.
        if (!mountedRef.current || lateCancel || waitingAttemptRef.current) return;
        setBusy(false);
        if (!finished) fetchNonce({ background: false, report: false });
      });
  };

  const notReady =
    libraryState === "loading" || (nonceState === "loading" && !busy);

  return { open, busy, notReady };
};
